/**
 * `useFunnelAnalytics` — emits the events in `secretmate_onboarding_funnel_plan.md` §8 (and PLAN.md §6.4) to the
 * `apps/analytics-api` Worker. Brand-agnostic: every event includes the
 * resolved `application_id` + `funnel_id` + `override_id` from runtime
 * context, plus a sticky `anonymous_id` for variant bucketing.
 *
 * The hook returns a stable bag of named emitters. Component callsites
 * call `funnel_step_viewed({ stepId })` etc. — they never assemble the
 * full event envelope themselves.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { buildEventId } from "@nextday/funnel-events";
import type { PixelFireFn } from "@src/tracking/usePixelTracking";
import { createEventQueue, type EventQueue } from "@nextday/tracking";
import { fireGa4Event } from "@src/tracking/ga4";

/** Discriminated union of every funnel-platform event (see plan §8). */
export type FunnelAnalyticsEventName =
  | "funnel_landing_viewed"
  | "funnel_started"
  | "funnel_step_viewed"
  | "funnel_answer_selected"
  | "funnel_step_completed"
  | "funnel_back_clicked"
  | "funnel_email_submitted"
  | "funnel_signup_completed"
  | "funnel_handoff_clicked"
  | "funnel_loading_viewed"
  | "funnel_character_revealed"
  | "funnel_first_message_previewed"
  | "paywall_viewed"
  | "plan_selected"
  | "purchase_completed"
  | "funnel_abandoned";

/** Conversion events must be sent immediately so they survive an external redirect. */
const ALWAYS_DIRECT_EVENTS: ReadonlySet<FunnelAnalyticsEventName> = new Set([
  "funnel_signup_completed",
  "funnel_handoff_clicked",
]);

export type FunnelAnalyticsContext = {
  applicationId: string;
  funnelId: string;
  overrideId: string | null;
  sessionId: string;
  anonymousId: string;
  userId?: string;
  /** Running experiment id when the session was assigned via experiment bucketing (NX-9773). */
  experimentId?: string;
  /** NX-9949: Experiment arm id assigned via bucketing. */
  armId?: string;
  /** NX-9847: Published funnel version number. Omitted when serving from draft tables. */
  funnelVersion?: number;
  /** Best-effort device classification (drives §8.2 `device.type`). */
  deviceType?: "mobile" | "desktop" | "tablet";
  /** Flattened attribution payload from localStorage — attached to every event. */
  attribution?: Record<string, string | undefined>;
};

type EventPayload = Record<string, unknown>;

export type FunnelAnalyticsEnvelope = EventPayload & {
  event: FunnelAnalyticsEventName;
  timestamp: string;
  event_id: string;
  application_id: string;
  funnel_id: string;
  override_id?: string;
  session_id: string;
  anonymous_id: string;
  user_id?: string;
  experiment_id?: string;
  arm_id?: string;
  funnel_version?: number;
  device_type?: "mobile" | "desktop" | "tablet";
};

const BATCH_PATH = "/api/events/batch" as const;

/** Module-level singleton queue — shared across all hook instances. */
let sharedQueue: EventQueue | null = null;

const getQueue = (): EventQueue => {
  if (sharedQueue === null) {
    sharedQueue = createEventQueue({
      endpoint: BATCH_PATH,
      flushIntervalMs: 500,
      maxBatchSize: 20,
    });
  }
  return sharedQueue;
};

/** Dispose and reset the singleton queue. Exposed for test teardown. */
export const _resetEventQueue = (): void => {
  if (sharedQueue !== null) {
    sharedQueue.dispose();
    sharedQueue = null;
  }
};

/**
 * Synchronously flush all buffered analytics events via `sendBeacon`.
 * Call before navigating away (e.g. `window.location.assign`) to ensure
 * events are not lost when the page unloads before the periodic timer fires.
 */
export const flushEventQueueSync = (): void => {
  if (sharedQueue !== null) {
    sharedQueue.flushSync();
  }
};

const buildEnvelope = (
  context: FunnelAnalyticsContext,
  event: FunnelAnalyticsEventName,
  data: EventPayload,
): FunnelAnalyticsEnvelope => {
  const timestamp = new Date().toISOString();
  const stepId = typeof data.step_id === "string" ? data.step_id : undefined;
  return {
    ...data,
    event,
    timestamp,
    event_id: buildEventId(context.sessionId, event, stepId, timestamp),
    application_id: context.applicationId,
    funnel_id: context.funnelId,
    ...(context.overrideId !== null ? { override_id: context.overrideId } : {}),
    session_id: context.sessionId,
    anonymous_id: context.anonymousId,
    ...(context.userId ? { user_id: context.userId } : {}),
    ...(context.experimentId ? { experiment_id: context.experimentId } : {}),
    ...(context.armId ? { arm_id: context.armId } : {}),
    ...(context.funnelVersion !== undefined ? { funnel_version: context.funnelVersion } : {}),
    ...(context.deviceType ? { device_type: context.deviceType } : {}),
    ...(context.attribution ? { attribution: context.attribution } : {}),
  };
};

const enqueueEvent = (envelope: FunnelAnalyticsEnvelope): void => {
  const body = JSON.stringify(envelope);
  try {
    getQueue().enqueue(body);
  } catch {
    // Fallback: wrap in a single-element array and send to the batch
    // endpoint so the server always receives the same shape.
    sendDirect(body);
  }
};

/**
 * NX-10230: Send a single event directly via sendBeacon (or fetch+keepalive
 * fallback) without going through the queue. Used for single-step funnels
 * where the page navigates away immediately and the queue/timer/flush
 * lifecycle cannot be relied upon.
 */
const sendDirect = (body: string): void => {
  const batchBody = `[${body}]`;
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const blob = new Blob([batchBody], { type: "application/json" });
    if (navigator.sendBeacon(BATCH_PATH, blob)) return;
  }
  if (typeof fetch === "function") {
    void fetch(BATCH_PATH, {
      method: "POST",
      keepalive: true,
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      body: batchBody,
    }).catch(() => undefined);
  }
};

export type FunnelAnalyticsApi = {
  emit: (event: FunnelAnalyticsEventName, data?: EventPayload) => void;
  // Convenience wrappers for the most-used events. Callsites that emit a
  // novel event can fall back to `emit(...)` directly.
  funnel_landing_viewed: (data?: EventPayload) => void;
  funnel_started: (data?: EventPayload) => void;
  funnel_step_viewed: (data: { stepId: string; stepIndex?: number }) => void;
  funnel_answer_selected: (data: {
    stepId: string;
    stepIndex?: number;
    stepType?: string;
    answerKey: string;
    value: unknown;
  }) => void;
  funnel_step_completed: (data: {
    stepId: string;
    stepIndex?: number;
    answers?: Record<string, unknown>;
    duration_ms?: number;
    immediate?: boolean;
  }) => void;
  funnel_back_clicked: (data: { fromStepId: string }) => void;
  funnel_loading_viewed: (data: { stepId: string }) => void;
  funnel_character_revealed: (data: { stepId: string; characterId?: string }) => void;
  funnel_first_message_previewed: (data: { stepId: string; characterId?: string }) => void;
  paywall_viewed: (data: { stepId: string }) => void;
};

/**
 * Hook returning a stable analytics emitter bag for the current funnel
 * context. The emitters are memoized across renders so they're safe to
 * pass directly to renderer props without triggering re-renders.
 *
 * When `options.directMode` is `true`, every `emit()` call sends the event
 * immediately via `sendBeacon` (or fetch+keepalive fallback) instead of
 * buffering in the shared EventQueue. Use this for single-step funnels
 * where the page navigates away before the queue timer can fire (NX-10230).
 */
export const useFunnelAnalytics = (
  context: FunnelAnalyticsContext,
  options?: { onPixelFire?: PixelFireFn; directMode?: boolean },
): FunnelAnalyticsApi => {
  // Hold the most recent context in a ref so `emit`'s identity stays
  // stable across renders. Most callers construct `context` inline (a
  // new object every render); if `emit` depended on `context` directly
  // it would change reference every render, recreate every convenience
  // wrapper, and force every consumer that lists the wrappers in their
  // own deps to re-run — defeating the JSDoc promise of a stable
  // emitter bag. Mirroring `usePaidAdLint`'s pattern in this PR.
  const contextRef = useRef(context);
  contextRef.current = context;

  const pixelFireRef = useRef(options?.onPixelFire);
  pixelFireRef.current = options?.onPixelFire;

  // Flush (not dispose) on unmount — the singleton queue persists across
  // component re-mounts in the SPA.  dispose() is only called via
  // _resetEventQueue() in tests or by the EventQueue's own pagehide
  // listener when the page is actually unloading.
  useEffect(() => {
    return () => {
      void getQueue().flush();
    };
  }, []);

  const directModeRef = useRef(!!options?.directMode);
  directModeRef.current = !!options?.directMode;

  const emitInternal = useCallback(
    (event: FunnelAnalyticsEventName, data: EventPayload = {}, forceDirect = false): void => {
      const envelope = buildEnvelope(contextRef.current, event, data);
      if (forceDirect || ALWAYS_DIRECT_EVENTS.has(event) || directModeRef.current) {
        sendDirect(JSON.stringify(envelope));
      } else {
        enqueueEvent(envelope);
      }
      pixelFireRef.current?.(event, data);
      fireGa4Event(envelope);
    },
    [],
  );

  const emit = useCallback<FunnelAnalyticsApi["emit"]>(
    (event, data = {}) => {
      emitInternal(event, data);
    },
    [emitInternal],
  );

  /*
   * Keep the public `emit` signature unchanged while allowing the
   * funnel_step_completed wrapper to force-direct terminal-ish steps.
   */
  const emitStepCompleted = useCallback(
    (data: {
      stepId: string;
      stepIndex?: number;
      answers?: Record<string, unknown>;
      duration_ms?: number;
      immediate?: boolean;
    }): void => {
      const { immediate, ...stepData } = data;
      emitInternal(
        "funnel_step_completed",
        {
          step_id: stepData.stepId,
          ...(stepData.stepIndex !== undefined ? { step_index: stepData.stepIndex } : {}),
          ...(stepData.answers !== undefined ? { answers: stepData.answers } : {}),
          ...(stepData.duration_ms !== undefined && stepData.duration_ms > 0
            ? { duration_ms: stepData.duration_ms }
            : {}),
        },
        immediate === true,
      );
    },
    [emitInternal],
  );

  return useMemo<FunnelAnalyticsApi>(
    () => ({
      emit,
      funnel_landing_viewed: (data) => emit("funnel_landing_viewed", data),
      funnel_started: (data) => emit("funnel_started", data),
      funnel_step_viewed: (data) =>
        emit("funnel_step_viewed", {
          step_id: data.stepId,
          ...(data.stepIndex !== undefined ? { step_index: data.stepIndex } : {}),
        }),
      funnel_answer_selected: (data) =>
        emit("funnel_answer_selected", {
          step_id: data.stepId,
          ...(data.stepIndex !== undefined ? { step_index: data.stepIndex } : {}),
          ...(data.stepType !== undefined ? { step_type: data.stepType } : {}),
          answer_key: data.answerKey,
          value: data.value,
        }),
      funnel_step_completed: emitStepCompleted,
      funnel_back_clicked: (data) => emit("funnel_back_clicked", { from_step_id: data.fromStepId }),
      funnel_loading_viewed: (data) => emit("funnel_loading_viewed", { step_id: data.stepId }),
      funnel_character_revealed: (data) =>
        emit("funnel_character_revealed", {
          step_id: data.stepId,
          ...(data.characterId !== undefined ? { character_id: data.characterId } : {}),
        }),
      funnel_first_message_previewed: (data) =>
        emit("funnel_first_message_previewed", {
          step_id: data.stepId,
          ...(data.characterId !== undefined ? { character_id: data.characterId } : {}),
        }),
      paywall_viewed: (data) => emit("paywall_viewed", { step_id: data.stepId }),
    }),
    [emit, emitStepCompleted],
  );
};
